/**
 * Centralized auth token store.
 *
 * Single source of truth for reading/writing/clearing auth tokens in
 * sessionStorage. All consumers (api-client, trpc, dzi-cache, image viewer,
 * thumbnails, AuthContext) MUST go through this module rather than touching
 * sessionStorage directly. This guarantees:
 *
 *   - Corrupted JSON never throws into the caller (parse errors clear storage)
 *   - All token mutations notify subscribers, so React state stays in sync
 *     with apiClient-internal token refreshes
 *   - There is exactly one storage key to rotate or harden later
 */

import type { AuthTokens } from '@/types/auth';

const STORAGE_KEY = 'auth_tokens';

// Dev only: keep tokens in localStorage (shared across tabs of the origin)
// instead of sessionStorage (per tab), so local debugging doesn't require a
// fresh login in every tab. Production keeps the stricter per-tab scope.
const storage: Storage = import.meta.env.DEV ? localStorage : sessionStorage;

let inMemoryTokens: AuthTokens | null = null;
let hydrated = false;
const subscribers = new Set<(tokens: AuthTokens | null) => void>();

function readFromStorage(): AuthTokens | null {
  try {
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as AuthTokens;
    if (
      !parsed ||
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.idToken !== 'string' ||
      typeof parsed.refreshToken !== 'string'
    ) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    // Corrupted payload — drop it rather than throwing into the caller.
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

function notify(tokens: AuthTokens | null) {
  for (const listener of subscribers) {
    try {
      listener(tokens);
    } catch (err) {
      // A misbehaving subscriber must not break other subscribers.
      console.error('[auth-token] subscriber threw:', err);
    }
  }
}

/**
 * Get the currently stored tokens. Lazily hydrates from sessionStorage on
 * first call so reloads work without an explicit init step.
 */
export function getStoredTokens(): AuthTokens | null {
  if (!hydrated) {
    inMemoryTokens = readFromStorage();
    hydrated = true;
  }
  return inMemoryTokens;
}

/**
 * Persist tokens to sessionStorage and notify subscribers.
 * Pass `null` to clear.
 */
export function setStoredTokens(tokens: AuthTokens | null): void {
  inMemoryTokens = tokens;
  hydrated = true;
  try {
    if (tokens) {
      storage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } else {
      storage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    // Quota errors / disabled storage — keep the in-memory copy so the
    // current session still works, but log so we notice.
    console.error('[auth-token] failed to persist tokens:', err);
  }
  notify(tokens);
}

export function clearStoredTokens(): void {
  setStoredTokens(null);
}

// Cross-tab sync: the `storage` event fires in OTHER tabs of the origin when
// this tab writes the key. Without this, a login/logout in one tab leaves
// other tabs running on stale in-memory tokens until a manual reload —
// especially confusing in DEV where tokens are shared via localStorage while
// switching between test accounts. (sessionStorage never fires cross-tab, so
// this is inert in production.)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY && e.key !== null) return;
    hydrated = false;
    notify(getStoredTokens());
  });
}

/**
 * Subscribe to token changes. Returns an unsubscribe function.
 * Used by AuthContext (and any other reactive consumer) to stay in sync
 * when apiClient performs an internal refresh.
 */
export function subscribeToTokens(
  listener: (tokens: AuthTokens | null) => void
): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/**
 * Build the full set of auth headers expected by the trpc-server and the
 * /data/* REST shims. Sends both:
 *   - Authorization: Bearer <accessToken>   (verified by protectedProcedure)
 *   - X-Cognito-Id-Token: <idToken>         (used by the data router to vend
 *                                            S3 credentials via Cognito Identity)
 *
 * Returns an empty object if no tokens are stored, so callers can spread the
 * result unconditionally into a headers object.
 *
 * Do NOT use this for requests that go directly to the dev backend
 * (https://dev.eo.lecole.ai/api). That backend only knows the access token;
 * api-client.ts handles those requests internally.
 */
export function getAuthHeaders(): Record<string, string> {
  const tokens = getStoredTokens();
  if (!tokens) return {};
  return {
    Authorization: `Bearer ${tokens.accessToken}`,
    'X-Cognito-Id-Token': tokens.idToken,
  };
}
